import React, { useRef, useState } from "react";

import { Icon, Loader } from "@culturehq/components";

const PlaybackGrabber = ({
  cursorMarkerRef,
  onCursorMarkerRefUpdateLeft,
  onCursorMarkerRefUpdateDisplay,
  onVideoRefUpdateTime,
  playerEndRef,
  playerStartRef,
  setPlayerEnd,
  setPlayerStart,
  setVideoTime,
  thumbnail,
  videoRef,
  videoTime
}) => {
  const playbackBarRef = useRef();
  const seekableBarRef = useRef();
  const notSeekableBarLeftRef = useRef();
  const notSeekableBarRightRef = useRef();
  const startGrabberRef = useRef();
  const endGrabberRef = useRef();
  const [playbackRect, setPlaybackRect] = useState(null);
  const [frontTrimmedRatio, setFrontTrimmedRatio] = useState(0);
  const [endTrimmedRatio, setEndTrimmedRatio] = useState(0);

  const playbackRectRef = useRef();
  const endTrimmedRatioRef = useRef();
  const frontTrimmedRatioRef = useRef();
  playbackRectRef.current = playbackRect;
  endTrimmedRatioRef.current = endTrimmedRatio;
  frontTrimmedRatioRef.current = frontTrimmedRatio;

  const pauseVideo = () => {
    videoRef.current.pause();
  };

  const handleSeekMouseDown = event => {
    pauseVideo();
    const rect = playbackBarRef.current.getBoundingClientRect();
    const seekAmount = (event.clientX - rect.left) / rect.width;
    setPlaybackRect(rect);
    seekVideo(seekAmount);
    window.addEventListener("mousemove", playheadMove);
  };

  const playheadMove = event => {
    const seekAmount = (event.clientX - playbackRectRef.current.left)
      / playbackRectRef.current.width;
    seekVideo(seekAmount);
    window.addEventListener("mouseup", playheadDrop);
  };

  const playheadDrop = () => {
    window.removeEventListener("mousemove", playheadMove);
    window.removeEventListener("mouseup", playheadDrop);
  };

  const handleSeekableBarClick = event => {
    pauseVideo();
    const rect = playbackBarRef.current.getBoundingClientRect();
    const seekAmount = (event.clientX - playbackRectRef.current.left)
      / playbackRectRef.current.width;
    setPlaybackRect(rect);
    seekVideo(seekAmount);
    window.removeEventListener("mousemove", playheadMove);
  };

  const updateSeekableStart = () => {
    setPlayerStart(videoRef.current.duration * frontTrimmedRatioRef.current);
    setVideoTime(playerEndRef.current - (videoRef.current.duration * frontTrimmedRatioRef.current));
    seekableBarRef.current.style.left = `${frontTrimmedRatioRef.current * 100}%`;
    notSeekableBarLeftRef.current.style.width = `calc(${frontTrimmedRatioRef.current * 100}% - 1px)`;
    startGrabberRef.current.style.left = `${frontTrimmedRatioRef.current * 100}%`;
  };

  const updateSeekableEnd = () => {
    setPlayerEnd(videoRef.current.duration * endTrimmedRatioRef.current);
    setVideoTime((videoRef.current.duration * endTrimmedRatioRef.current) - playerStartRef.current);
    seekableBarRef.current.style.right = `${(1 - endTrimmedRatioRef.current) * 100}%`;
    notSeekableBarRightRef.current.style.width = `${100 - (endTrimmedRatioRef.current * 100)}%`;
    notSeekableBarRightRef.current.style.left = `calc(${endTrimmedRatioRef.current * 100}% + 25px)`;
    endGrabberRef.current.style.left = `${endTrimmedRatioRef.current * 100}%`;
  };

  const startGrabberMove = event => {
    onCursorMarkerRefUpdateDisplay("none");
    const ratio = (event.clientX - playbackRectRef.current.left) / playbackRectRef.current.width;
    if (ratio <= 0) {
      setFrontTrimmedRatio(0);
    } else if (ratio >= endTrimmedRatioRef.current && endTrimmedRatioRef.current !== 0) {
      setFrontTrimmedRatio(endTrimmedRatioRef.current);
    } else {
      setFrontTrimmedRatio(ratio);
    }
    updateSeekableStart();
    seekVideo(frontTrimmedRatioRef.current);
    updateProgressBar();
  };

  const endGrabberMove = event => {
    onCursorMarkerRefUpdateDisplay("none");
    const ratio = (event.clientX - playbackRectRef.current.left) / playbackRectRef.current.width;
    if (ratio >= 1) {
      setEndTrimmedRatio(1);
      seekVideo(1);
    } else if (ratio <= frontTrimmedRatioRef.current) {
      setEndTrimmedRatio(frontTrimmedRatioRef.current);
      seekVideo(frontTrimmedRatioRef.current);
    } else {
      setEndTrimmedRatio(ratio);
      seekVideo(ratio);
    }
    updateSeekableEnd();
    updateProgressBar();
  };

  const onStartMouseDown = () => {
    setPlaybackRect(playbackBarRef.current.getBoundingClientRect());
    window.addEventListener("mousemove", startGrabberMove);
    window.addEventListener("mouseup", startGrabberDrop);
  };

  const onEndMouseDown = () => {
    setPlaybackRect(playbackBarRef.current.getBoundingClientRect());
    window.addEventListener("mousemove", endGrabberMove);
    window.addEventListener("mouseup", endGrabberDrop);
  };

  const updateProgressBar = () => {
    onCursorMarkerRefUpdateLeft(`${frontTrimmedRatioRef.current * 100}%`);
  };

  const startGrabberDrop = () => {
    onCursorMarkerRefUpdateDisplay("block");
    setVideoTime(playerEndRef.current - playerStartRef.current);
    window.removeEventListener("mousemove", startGrabberMove);
    window.removeEventListener("mouseup", startGrabberDrop);
  };

  const endGrabberDrop = () => {
    onCursorMarkerRefUpdateDisplay("block");
    window.removeEventListener("mousemove", endGrabberMove);
    window.removeEventListener("mouseup", endGrabberDrop);
  };

  const seekVideo = seekRatio => {
    if (videoRef.current.duration * seekRatio !== Infinity) {
      onVideoRefUpdateTime(videoRef.current.duration * seekRatio);
    }
  };

  if (!Number.isFinite(videoTime)) {
    return <Loader loading />;
  }

  return (
    <div className="playback" ref={playbackBarRef}>
      <div className="grabber start" onMouseDown={onStartMouseDown} ref={startGrabberRef}>
        <Icon icon="playback-grabber" />
      </div>
      <div className="grabber end" onMouseDown={onEndMouseDown} ref={endGrabberRef}>
        <Icon icon="playback-grabber" />
      </div>
      <div
        className="Story_Preview Story"
        style={{ backgroundImage: `url(${thumbnail})` }}
      />
      <div className="seekable" ref={seekableBarRef} onMouseDown={handleSeekMouseDown} onClick={handleSeekableBarClick} />
      <div className="not-seekable not-seekable-left" ref={notSeekableBarLeftRef} />
      <div className="not-seekable not-seekable-right" ref={notSeekableBarRightRef} />
      <div className="cursor-marker" ref={cursorMarkerRef} onMouseDown={handleSeekMouseDown} />
    </div>
  );
};

export default PlaybackGrabber;
